import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { AIInsightBanner } from "../components/AIInsightBanner";
import { PageTimeline } from "../components/PageTimeline";
import { KpiCard } from "../components/KpiCard";
import { usePeriod } from "../lib/PeriodContext";
import api from "../lib/api";
import { fmtINR, fmtPct } from "../lib/format";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend,
} from "recharts";
import { ResponsiveContainer } from "../components/AutoSizer";
import { useAuth } from "../lib/AuthContext";

const palette = ["#0F1D32", "#D4AF37", "#475569", "#3730A3", "#991B1B"];

export default function CollectionIntelligence() {
  const { asOf, period, projectsQS } = usePeriod();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [edit, setEdit] = useState({}); // { "project|period": value }
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (!asOf) return;
    api.get(`/kpi/executive?as_of=${asOf}&period=${period}${projectsQS}`).then((r) => setData(r.data));
    api.get(`/kpi/summary-tables?as_of=${asOf}&period=${period}${projectsQS}`).then((r) => setSummary(r.data));
    loadMatrix();
  }, [asOf, period, projectsQS]);

  const loadMatrix = async () => {
    const [tgts, proj] = await Promise.all([
      api.get("/targets/manage?kind=collection"),
      api.get("/projects/all"),
    ]);
    setMatrix({ targets: tgts.data.targets, projects: proj.data.projects });
  };

  const monthsList = matrix ? [...new Set(matrix.targets.map((t) => t.period))].sort() : [];

  const saveCell = async (pname, periodStr) => {
    const key = `${pname}|${periodStr}`;
    const val = parseFloat(draft[key]);
    if (isNaN(val)) { toast.error("Enter a valid number"); return; }
    try {
      await api.post("/targets/manage", {
        project_name: pname, period: periodStr, kind: "collection", target_value: val,
      });
      toast.success("Saved");
      setEdit({ ...edit, [key]: false });
      loadMatrix();
      // Refresh KPI cards too (cache busted server-side)
      api.get(`/kpi/executive?as_of=${asOf}&period=${period}${projectsQS}`).then((r) => setData(r.data));
    } catch (e) {
      toast.error("Save failed");
    }
  };

  if (!data) return <><Header title="Collection Intelligence" /><div className="p-6">Loading...</div></>;

  const k = data.kpis;
  const projColl = data.project_collection || [];
  const totalColl = projColl.reduce((a, b) => a + b.collection, 0);
  const receipts = summary?.receipt_breakdown || {};
  const receiptData = Object.entries(receipts).map(([name, value]) => ({ name, value }));

  const canEdit = user?.role === "admin" || user?.role === "manager";

  return (
    <>
      <Header title="Collection Intelligence" subtitle="Cash Flow · Receipts · Targets" />
      <div className="p-3 sm:p-6 md:p-8 max-w-[1600px] mx-auto space-y-5 min-w-0">
        <PageTimeline fyLabel={data.fy_label} />
        <AIInsightBanner context={{ period: data.period, fy_label: data.fy_label, kpis: k }} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Target" value={fmtINR(k.collection_target, { short: true })} />
          <KpiCard label="Actual" value={fmtINR(k.collection, { short: true })} delta={data.delta?.collection} accent />
          <KpiCard label="Efficiency" value={fmtPct(k.collection_efficiency)} />
          <KpiCard label="Gap" value={fmtINR(Math.max(0, k.collection_target - k.collection), { short: true })} dark />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-md border border-slate-200 p-4 lg:col-span-2">
            <div className="text-sm font-semibold mb-3">Collection by Project</div>
            <ResponsiveContainer width="100%" height={Math.max(280, projColl.length * 22)}>
              <BarChart data={projColl} layout="vertical" margin={{ left: 110 }}>
                <CartesianGrid stroke="#e2e8f0" />
                <XAxis type="number" tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="project_name" width={150} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `${fmtINR(v, { short: true })} (${((v / totalColl) * 100).toFixed(1)}%)`} />
                <Bar dataKey="collection" fill="#D4AF37" radius={[0, 4, 4, 0]} name="Collection" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">Receipt Type Mix</div>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={receiptData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                  {receiptData.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtINR(v, { short: true })} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-md border border-slate-200 p-4">
          <div className="text-sm font-semibold mb-3">Monthly Collection Trend</div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.monthly_trend}>
              <defs>
                <linearGradient id="cgrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtINR(v, { short: true })} />
              <Area dataKey="collection" fill="url(#cgrad)" stroke="#D4AF37" strokeWidth={2} name="Collection" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Collection Targets Matrix */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-semibold">Collection Targets · Monthly Matrix</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                {canEdit ? "Tap any cell to edit · values in ₹ Crore" : "View-only · Admin/Manager only"}
              </div>
            </div>
          </div>
          <div className="matrix-wrap" data-testid="collection-targets-matrix">
            <table>
              <colgroup>
                <col style={{ width: 220 }} />
                {monthsList.map((m) => <col key={m} style={{ width: 110 }} />)}
                <col style={{ width: 110 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="sticky-col text-left">Project</th>
                  {monthsList.map((m) => (
                    <th key={m} className="numeric-cell text-[10px] uppercase tracking-wider">
                      {new Date(m).toLocaleString("en-US", { month: "short", year: "2-digit" })}
                    </th>
                  ))}
                  <th className="numeric-cell">Total</th>
                </tr>
              </thead>
              <tbody>
                {matrix && matrix.projects.map((p) => {
                  let rowTotal = 0;
                  return (
                    <tr key={p.project_name}>
                      <td className="sticky-col truncate">{p.project_name}</td>
                      {monthsList.map((m) => {
                        const t = matrix.targets.find((x) => x.project_name === p.project_name && x.period === m);
                        const key = `${p.project_name}|${m}`;
                        const isEditing = !!edit[key];
                        const val = t?.target_value || 0;
                        rowTotal += val;
                        return (
                          <td key={m} className="numeric-cell">
                            {isEditing ? (
                              <div className="flex items-center gap-1">
                                <input
                                  className="w-16 px-1 py-0.5 border border-slate-300 rounded text-right font-mono text-xs"
                                  value={draft[key] ?? val}
                                  onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                                  onKeyDown={(e) => { if (e.key === "Enter") saveCell(p.project_name, m); }}
                                  data-testid={`coll-target-input-${p.project_name}-${m}`}
                                  autoFocus
                                />
                                <button onClick={() => saveCell(p.project_name, m)} className="text-emerald-600" data-testid={`save-coll-${p.project_name}-${m}`}>
                                  <Check size={12} />
                                </button>
                                <button onClick={() => setEdit({ ...edit, [key]: false })} className="text-red-600">
                                  <X size={12} />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => canEdit && (setEdit({ ...edit, [key]: true }), setDraft({ ...draft, [key]: val }))}
                                disabled={!canEdit}
                                className={`w-full text-right ${canEdit ? "cursor-pointer hover:bg-amber-50 rounded px-1 -mx-1" : "cursor-default"}`}
                                data-testid={`edit-coll-${p.project_name}-${m}`}
                              >
                                {val.toFixed(1)}
                              </button>
                            )}
                          </td>
                        );
                      })}
                      <td className="numeric-cell font-semibold">{rowTotal.toFixed(1)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {matrix && (
                <tr>
                  <td className="sticky-col">Total</td>
                  {monthsList.map((m) => {
                    const total = matrix.targets.filter((t) => t.period === m).reduce((a, b) => a + b.target_value, 0);
                    return <td key={m} className="numeric-cell">{total.toFixed(1)}</td>;
                  })}
                  <td className="numeric-cell">
                    {matrix.targets.reduce((a, b) => a + b.target_value, 0).toFixed(1)}
                  </td>
                </tr>
                )}
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
